/**
 * GuestViewContext
 *
 * Provides guest view mode state for HMC Events page.
 * In guest mode:
 * - Report history sidebar is hidden
 * - Ticket availability is capped at "4 max available"
 * - Last updated timestamps are hidden from cards, tables, and modals
 *
 * Can be initialized in forced guest mode via `initialGuestMode` prop for
 * shareable links (guests cannot toggle back to admin mode).
 */

import * as React from 'react';

// ============================================================================
// Types
// ============================================================================

interface GuestViewContextType {
  /** Whether guest view mode is active */
  isGuestView: boolean;
  /** Toggle between guest and admin view (disabled in forced guest mode) */
  toggleGuestView: () => void;
  /** Maximum tickets to display in guest mode */
  maxGuestTickets: number;
  /** Whether guest mode is forced (shareable link access) */
  isForcedGuestMode: boolean;
}

interface GuestViewProviderProps {
  children: React.ReactNode;
  /** Force guest mode on (for shareable links - cannot be toggled off) */
  initialGuestMode?: boolean;
}

// ============================================================================
// Default context value (prevents TDZ errors in bundled code)
// ============================================================================

// Default value used when context is accessed before provider is mounted
// This prevents "Cannot access X before initialization" errors from bundler reordering
const defaultContextValue: GuestViewContextType = {
  isGuestView: false,
  toggleGuestView: () => {
    console.warn('GuestViewContext: toggleGuestView called before provider mounted');
  },
  maxGuestTickets: 4,
  isForcedGuestMode: false,
};

// ============================================================================
// Context Creation (with default value to prevent TDZ errors)
// ============================================================================

// Use React.createContext with a default value so it never returns undefined
// This is crucial for esbuild bundling which can reorder module initialization
const GuestViewContext = React.createContext<GuestViewContextType>(defaultContextValue);

// ============================================================================
// Provider Component
// ============================================================================

function GuestViewProvider({ children, initialGuestMode = false }: GuestViewProviderProps): React.ReactElement {
  const [isGuestView, setIsGuestView] = React.useState(initialGuestMode);

  const toggleGuestView = React.useCallback(() => {
    // Don't allow toggling if in forced guest mode
    if (initialGuestMode) return;
    setIsGuestView((prev) => !prev);
  }, [initialGuestMode]);

  const value = React.useMemo(
    () => ({
      isGuestView,
      toggleGuestView,
      maxGuestTickets: 4,
      isForcedGuestMode: initialGuestMode,
    }),
    [isGuestView, toggleGuestView, initialGuestMode]
  );

  return React.createElement(GuestViewContext.Provider, { value }, children);
}

// ============================================================================
// Hooks
// ============================================================================

function useGuestView(): GuestViewContextType {
  // No need to check for undefined since we have a default value
  // This prevents TDZ errors when bundler reorders module initialization
  return React.useContext(GuestViewContext);
}

/**
 * Hook to get guest-adjusted ticket count
 * In guest mode, caps remaining tickets at maxGuestTickets
 */
function useGuestTickets(remaining: number, capacity: number) {
  const { isGuestView, maxGuestTickets } = useGuestView();

  if (isGuestView) {
    // In guest mode, show max 4 available and hide total capacity
    return {
      remaining: Math.min(remaining, maxGuestTickets),
      capacity: maxGuestTickets, // Hide real capacity
      displayMode: 'guest' as const,
    };
  }

  return {
    remaining,
    capacity,
    displayMode: 'admin' as const,
  };
}

// ============================================================================
// Exports (grouped together to avoid bundler reordering issues)
// ============================================================================

export { GuestViewContext, GuestViewProvider, useGuestView, useGuestTickets };
export type { GuestViewContextType, GuestViewProviderProps };
